import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import Button from 'components/base/Button';
import dayjs from 'dayjs';
import { errorHandler } from 'helpers/response-utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Col, Modal, Row, Tab, Tabs } from 'react-bootstrap';
import {
  useNavigate,
  useParams,
  useOutletContext,
  useLocation
} from 'react-router-dom';
import AddGoals from './AddGoals';
import AddGoalProgress from './AddGoalProgress';
import GoalsChart from './GoalsChart';
import GoalsTable, { getGoalsTableColumns } from './GoalsTable';
import WeightChart from './WeightChart';
import HbA1CChart from './HbA1CChart';
import WeightGoalForm from './WeightGoalForm'; // New component for weight goals
import HbA1CGoalForm from './HbA1CGoalForm'; // New component for HbA1C goals
import { trackEvent } from '../../../../utils/analytics';

const GoalsList = () => {
  const navigate = useNavigate();
  const [goalsData, setGoalsData] = useState([]);
  const [goalsChartData, setGoalsChartData] = useState([]);
  const [selectedGoal, setSelectedGoal] = useState<any>(null);
  const token = localStorage.getItem('token');
  const { id } = useParams();

  const [showAddGoalModal, setShowAddGoalModal] = useState(false);
  const [showAddGoalProgressModal, setShowAddGoalProgressModal] =
    useState(false);
  const [showSetGoalButton, setShowSetGoalButton] = useState(false);
  const activeTab = location.pathname.includes('weight') ? 'weight' : 'hba1c';

  const { clinic } = useOutletContext<any>();

  const handleClose = (isAddedRecord: boolean) => {
    setShowAddGoalModal(false);
    setShowAddGoalProgressModal(false);
    if (isAddedRecord) {
      getList();
    }
  };

  useEffect(() => {
    getList();
  }, [activeTab]);

  useEffect(() => {
    if (goalsData.length > 0) {
      setSelectedGoal(goalsData[0]);
      getChartData(goalsData[0]['id']);
    }
  }, [goalsData]);

  const getList = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_ENDPOINT}/clinic/goals/patient/${id}/type/${activeTab}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (response.data['status'] == false) {
      errorHandler(response.data);
      return false;
    }

    const goals = response.data['data']['goal'];
    setGoalsData(goals);

    const allDatesPassed =
      goals.length > 0 &&
      goals.every((goal: any) => dayjs(goal.date).isBefore(dayjs(), 'day'));

    setShowSetGoalButton(goals.length === 0 || allDatesPassed);
  };

  const getChartData = async (goalID: number) => {
    const response = await axios.get(
      `${process.env.REACT_APP_ENDPOINT}/clinic/goal-progress/${goalID}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (response.data['status'] == false) {
      errorHandler(response.data);
      return false;
    }

    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

    trackEvent('goal_progress_added', {
      goal_type: activeTab,
      actor_user_id: String(currentUser.id || ''),
      actor_user_name: `${currentUser.firstname || ''} ${
        currentUser.lastname || ''
      }`.trim(),
      actor_role:
        currentUser.role?.toLowerCase() === 'clinic'
          ? 'clinic admin'
          : currentUser.role || '',
      platform: 'web'
    });

    setGoalsChartData(response.data['data']['goals']);
  };

  const handleShowProgress = (goalId: number) => {
    const goal = goalsData.find((g: any) => g.id === goalId);
    setSelectedGoal(goal);
    getChartData(goalId);
  };

  const table = useAdvanceTable({
    data: goalsData,
    columns: getGoalsTableColumns(activeTab),
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false,
    initialState: {
      columnVisibility: {}
    },
    meta: {
      onShowProgress: handleShowProgress
    }
  });

  //for updated goal progress items
  const updateProgress = async (progressId: number, value: number) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_ENDPOINT}/clinic/goal-progress/${progressId}`,
        { progress_value: value },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === false) {
        errorHandler(response.data);
        return;
      }

      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

      trackEvent('goal_updated', {
        goal_type: activeTab,
        actor_user_id: String(currentUser.id || ''),
        actor_user_name: `${currentUser.firstname || ''} ${
          currentUser.lastname || ''
        }`.trim(),
        actor_role:
          currentUser.role?.toLowerCase() === 'clinic'
            ? 'clinic admin'
            : currentUser.role || '',
        platform: 'web'
      });

      // refresh chart data after update
      if (selectedGoal?.id) {
        getChartData(selectedGoal.id);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const deleteProgress = async (progressId: number) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_ENDPOINT}/clinic/goal-progress/${progressId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === false) {
        errorHandler(response.data);
        return;
      }

      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

      trackEvent('goal_progress_deleted', {
        goal_type: activeTab,
        actor_user_id: String(currentUser.id || ''),
        actor_user_name: `${currentUser.firstname || ''} ${
          currentUser.lastname || ''
        }`.trim(),
        actor_role:
          currentUser.role?.toLowerCase() === 'clinic'
            ? 'clinic admin'
            : currentUser.role || '',
        platform: 'web'
      });

      // refresh data after delete
      if (selectedGoal?.id) {
        getChartData(selectedGoal.id);
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (!clinic?.isDisplayFoodData) {
    return (
      <div className="text-center mt-5">
        <h4>Contact MLAS Admin to enable this feature.</h4>
      </div>
    );
  }

  return (
    <>
      <Row className="mb-4">
        <Col className="d-flex justify-content-start gap-3">
          <h2>{activeTab === 'weight' ? 'Weight' : 'HbA1C'}</h2>
        </Col>
        <Col className="d-flex justify-content-end align-items-center gap-3">
          {activeTab === 'weight' ? (
            <>
              {showSetGoalButton ? (
                <Button
                  variant="primary"
                  onClick={() => setShowAddGoalModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Set Weight Goal
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => setShowAddGoalProgressModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add Weight
                </Button>
              )}
            </>
          ) : null}

          {activeTab === 'hba1c' ? (
            <>
              {showSetGoalButton ? (
                <Button
                  variant="primary"
                  onClick={() => setShowAddGoalModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Set HbA1C Goal
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => setShowAddGoalProgressModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add HbA1C
                </Button>
              )}
            </>
          ) : null}
        </Col>
      </Row>

      <Row>
        <Col xl={12}>
          <AdvanceTableProvider {...table}>
            <Row className="g-3 justify-content-between mb-2">
              <Col xs="auto"></Col>
            </Row>
            {/* onUpdateComplete={getList} */}
            <GoalsTable onShowProgress={handleShowProgress} />
            {goalsData.length === 0 ? (
              <div className="text-center text-muted py-4">
                No record found!
              </div>
            ) : null}
          </AdvanceTableProvider>

          <Modal
            show={showAddGoalModal}
            onHide={() => setShowAddGoalModal(false)}
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>
                Set {activeTab === 'weight' ? 'Weight' : 'HbA1C'} Goal
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {activeTab === 'weight' ? (
                <WeightGoalForm onClose={handleClose} patientId={id} />
              ) : null}
              {activeTab === 'hba1c' ? (
                <HbA1CGoalForm onClose={handleClose} patientId={id} />
              ) : null}
            </Modal.Body>
          </Modal>

          <Modal
            show={showAddGoalProgressModal}
            onHide={() => handleClose(false)}
            backdrop="static"
            size="lg"
            keyboard={false}
          >
            <Modal.Header closeButton>
              <Modal.Title>
                Add {activeTab === 'weight' ? 'Weight' : 'HbA1C'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <AddGoalProgress
                handleClose={handleClose}
                goalId={selectedGoal?.id}
              />
            </Modal.Body>
          </Modal>
        </Col>
      </Row>

      {goalsData.length === 0 ? null : (
        <Row className="mt-2">
          <Col xl={12}>
            {selectedGoal && activeTab === 'weight' && (
              <WeightChart
                goalsData={goalsChartData}
                selectedGoal={selectedGoal}
                onUpdate={updateProgress}
                onDelete={deleteProgress}
              />
            )}
            {selectedGoal && activeTab === 'hba1c' && (
              <HbA1CChart
                goalsData={goalsChartData}
                selectedGoal={selectedGoal}
                onUpdate={updateProgress}
                onDelete={deleteProgress}
              />
            )}
          </Col>
        </Row>
      )}
    </>
  );
};

export default GoalsList;
